// ============================================================================
//  shotSpec — specyfikacja zrzutów boiska dołączanych do zapytania o wycenę:
//  stały rozmiar 16:9, cztery widoki (3× 3D + rzut 2D z wymiarami), czysta
//  matematyka kadrów (kamera perspektywiczna / frustum orto) i wpis payloadu.
//  ZERO DOM / THREE → testowalne pod Node. Render robi courtShots.js.
//  Układ świata jak w Court: linia końcowa na Z=0, boisko rośnie w +Z, X = 0 w osi.
// ============================================================================

// Wszystkie zrzuty w tej samej rozdzielczości 16:9 (ustalenie z klientem 2026-09-03).
export const SHOT_SIZE = { width: 1600, height: 900 }

// Kolejność = kolejność w payloadzie i w panelu handlowca. `dir` to kierunek
// OD środka płyty DO kamery (jak w Stage.frameCourt); dystans i środek kadru
// liczy fitPerspective z rzutów narożników płyty i bryły kosza, więc kadr jest
// ciasny i kompletny dla każdego rozmiaru boiska. Kierunki dobrane wizualnie
// (CDP) do wzorców klienta z 2026-09-03.
export const SHOT_VIEWS = [
  { view: 'iso-far', kind: '3d', label: 'Widok izometryczny od strony boiska', dir: [-0.8, 0.7, 1.0] },
  { view: 'front', kind: '3d', label: 'Widok frontalny na kosz', dir: [0, 0.95, 1.05] },
  { view: 'iso-hoop', kind: '3d', label: 'Widok izometryczny od strony kosza', dir: [-1.0, 0.62, -0.7] },
  { view: 'top-2d', kind: '2d', label: 'Rzut z góry 2D z wymiarami' },
]

// Margines kadru 3D: ułamek połowy kadru zostawiany pusty z każdej strony.
export const SHOT_PAD = 0.06

// --- mini-algebra wektorów [x, y, z] (bez THREE, by moduł działał pod Node) ---
const norm = (v) => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l] }
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
const add = (a, b, s = 1) => [a[0] + b[0] * s, a[1] + b[1] * s, a[2] + b[2] * s]

// Baza kamery patrzącej wzdłuż -d z „górą" (0,1,0) — dokładnie jak
// Object3D.lookAt w three.js: z = d, x = up × z, y = z × x.
function cameraBasis(dir) {
  const d = norm(dir)
  const right = norm(cross([0, 1, 0], d))
  const up = cross(d, right)
  return { d, right, up }
}

// Dopasowanie kamery perspektywicznej do chmury punktów: kamera na kierunku
// `dir` od celu, dystans = najmniejszy, przy którym każdy punkt mieści się w
// pionowym i poziomym FOV pomniejszonym o `pad`; cel dosuwany iteracyjnie tak,
// by rzut zawartości był wyśrodkowany w kadrze (przesunięcie celu przesuwa też
// kamerę, więc rzuty przesuwają się niemal jednorodnie; zbieżność geometryczna
// ~×0,6 na iterację — 10 iteracji daje błąd środka < 0,1 % kadru).
// Zwraca { fov, target, position } (tablice [x, y, z]).
export function fitPerspective(points, { dir, fov = 45, aspect = 16 / 9, pad = SHOT_PAD, iterations = 10 }) {
  const { d, right, up } = cameraBasis(dir)
  const tanV = Math.tan((fov * Math.PI) / 360) * (1 - pad)
  const tanH = tanV * aspect
  let target = points.reduce((acc, p) => add(acc, p, 1 / points.length), [0, 0, 0])

  // Najmniejszy dystans mieszczący wszystkie punkty: głębokość punktu to
  // dist - (p-t)·d, warunek |y| ≤ głębokość·tanV (analogicznie x).
  const fitDist = (t) => points.reduce((dist, p) => {
    const rel = add(p, t, -1)
    const z = dot(rel, d)
    return Math.max(dist, Math.abs(dot(rel, up)) / tanV + z, Math.abs(dot(rel, right)) / tanH + z)
  }, 0)

  let dist = fitDist(target)
  for (let i = 0; i < iterations; i++) {
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity
    for (const p of points) {
      const rel = add(p, target, -1)
      const depth = dist - dot(rel, d)
      const u = dot(rel, right) / depth
      const v = dot(rel, up) / depth
      minU = Math.min(minU, u); maxU = Math.max(maxU, u)
      minV = Math.min(minV, v); maxV = Math.max(maxV, v)
    }
    target = add(add(target, right, ((minU + maxU) / 2) * dist), up, ((minV + maxV) / 2) * dist)
    dist = fitDist(target)
  }
  return { fov, target, position: add(target, d, dist) }
}

// Współrzędne kadru punktu dla kamery z fitPerspective/perspShot: [u, v] w
// [-1, 1] gdy punkt jest w kadrze (jak NDC three.js, bez near/far). Do testów
// i strojenia — nie używane w renderze.
export function frameCoords(p, { position, target, fov }, aspect = 16 / 9) {
  const { d, right, up } = cameraBasis(add(position, target, -1))
  const rel = add(p, position, -1)
  const depth = -dot(rel, d)
  const tanV = Math.tan((fov * Math.PI) / 360)
  return [dot(rel, right) / (depth * tanV * aspect), dot(rel, up) / (depth * tanV)]
}

// Kamera perspektywiczna dla widoku 3D: dopasowanie do czterech narożników
// płyty (Y=0) plus `extraPoints` (np. narożniki bryły kosza z courtShots).
export function perspShot(bounds, { dir, fov = 45, aspect = 16 / 9, pad = SHOT_PAD, extraPoints = [] }) {
  const hw = bounds.width / 2
  const hl = bounds.length / 2
  const cz = bounds.centerZ
  const corners = [[-hw, 0, cz - hl], [hw, 0, cz - hl], [-hw, 0, cz + hl], [hw, 0, cz + hl]]
  return fitPerspective([...corners, ...extraPoints], { dir, fov, aspect, pad })
}

// Zapas kadru 2D na linie wymiarowe: wymiar leży `off` poza płytą (courtDims:
// max(0.5, 6% szer.)), etykieta-sprite o wysokości labelH (Court._buildDims:
// max(0.4, 3.5% szer.)) jest wyśrodkowana na kresce — z boku wystaje jej
// połowa szerokości (tekst „10.84 m · 36 szt" ≈ 5–6 wysokości), góra/dół
// połowa wysokości. Mnożniki z zapasem, by tekst nie dotykał krawędzi.
export function dimsPad(width) {
  const off = Math.max(0.5, width * 0.06)
  const labelH = Math.max(0.4, width * 0.035)
  return { padX: off + 3 * labelH, padZ: off + labelH }
}

// Frustum kamery ortograficznej (widok z góry, +Z w dół ekranu jak Stage.ortho)
// wokół płyty z zapasem na wymiary, dopasowany do proporcji zrzutu.
export function orthoShot(bounds, { aspect = 16 / 9 }) {
  const { padX, padZ } = dimsPad(bounds.width)
  let halfW = bounds.width / 2 + padX
  let halfH = bounds.length / 2 + padZ
  if (halfW / aspect > halfH) halfH = halfW / aspect
  else halfW = halfH * aspect
  return {
    left: -halfW,
    right: halfW,
    top: halfH,
    bottom: -halfH,
    position: [0, 50, bounds.centerZ],
    target: [0, 0, bounds.centerZ],
    up: [0, 0, -1],
  }
}

// Zrzuty są dodatkiem do wyceny: brak funkcji przechwytującej albo jej błąd
// (utrata kontekstu WebGL, brak pamięci na kanwę) NIE MOŻE zablokować wysyłki —
// wtedy pusta tablica, a przyczyna idzie do konsoli.
export async function collectScreenshots(capture) {
  if (!capture) return []
  try {
    return await capture()
  } catch (err) {
    console.error('Zrzuty boiska do wyceny:', err)
    return []
  }
}

const PNG_PREFIX = 'data:image/png;base64,'

// Wpis tablicy `screenshots` w payloadzie wyceny: metadane widoku + czysty
// base64 (bez prefiksu data URL). Odrzuca inny format niż PNG — backend dostaje
// wyłącznie zadeklarowany `mime`.
export function screenshotEntry(viewSpec, dataUrl) {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith(PNG_PREFIX)) {
    throw new Error(`Zrzut „${viewSpec.view}" nie jest PNG (data URL: ${String(dataUrl).slice(0, 30)}…)`)
  }
  return {
    view: viewSpec.view,
    label: viewSpec.label,
    mime: 'image/png',
    width: SHOT_SIZE.width,
    height: SHOT_SIZE.height,
    data: dataUrl.slice(PNG_PREFIX.length),
  }
}
